import { useEffect, useMemo, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { toast } from 'sonner';
import { Plus, ClipboardList, Trash2, Send, FileEdit, Building2, Store } from 'lucide-react';
import { FormSheet } from '@/components/common/FormSheet';
import { FormField } from '@/components/common/FormField';
import { DialogEyebrow, SectionHeading } from '@/components/common/DialogPrimitives';
import { DialogTitle, DialogDescription } from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Button } from '@/components/ui/button';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { vendorsService } from '@/services/vendors';
import { inventoryService, warehousesService } from '@/services/inventory';
import {
  procurementService,
  type CreatePurchaseRequestPayload,
} from '@/services/procurement';
import type { ProcurementSourcing } from '@/types/procurement';
import { fmt } from '@/lib/format';
import { cn } from '@/lib/utils';
import { pickerLabel } from '@/lib/inventory-label';
import type { InventoryItem } from '@/types/inventory';

interface Props {
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

interface DraftLine {
  key: number;
  itemId: string;
  quantity: string;
  estimatedUnitPrice: string;
}

let LINE_KEY = 1;
const newLine = (): DraftLine => ({
  key: LINE_KEY++,
  itemId: '',
  quantity: '',
  estimatedUnitPrice: '',
});

export function PurchaseRequestFormSheet({ open, onOpenChange }: Props) {
  const qc = useQueryClient();
  const [sourcingType, setSourcingType] = useState<ProcurementSourcing>('VENDOR');
  const [vendorId, setVendorId] = useState('');
  const [warehouseId, setWarehouseId] = useState('');
  const [neededBy, setNeededBy] = useState('');
  const [justification, setJustification] = useState('');
  const [notes, setNotes] = useState('');
  const [submitForApproval, setSubmitForApproval] = useState(true);
  const [lines, setLines] = useState<DraftLine[]>([newLine()]);

  const vendorsQuery = useQuery({
    queryKey: ['vendors', { forSelect: true }],
    queryFn: () => vendorsService.list({ status: 'ACTIVE', pageSize: 200 }),
    enabled: open,
    staleTime: 60_000,
  });
  const itemsQuery = useQuery({
    queryKey: ['inventory', 'items', { status: 'ACTIVE', forPr: true, pickable: true, kind: 'RAW_MATERIAL' }],
    queryFn: () => inventoryService.list({ status: 'ACTIVE', pageSize: 200, pickable: true, kind: 'RAW_MATERIAL' }),
    enabled: open,
    staleTime: 30_000,
  });
  const warehousesQuery = useQuery({
    queryKey: ['inventory', 'warehouses'],
    queryFn: () => warehousesService.list(),
    enabled: open,
    staleTime: 60_000,
  });

  const vendors = vendorsQuery.data?.items ?? [];
  const items = itemsQuery.data?.items ?? [];
  const warehouses = warehousesQuery.data ?? [];

  // Only raw materials are requestable. Existing/finished-good SKUs are
  // tagged FINISHED_GOOD server-side and excluded here.
  const rawItems = useMemo(
    () => items.filter((i) => i.kind === 'RAW_MATERIAL' && !i.isBundle),
    [items],
  );
  const itemById = useMemo(() => {
    const m = new Map<string, InventoryItem>();
    for (const it of items) m.set(it.id, it);
    return m;
  }, [items]);

  useEffect(() => {
    if (!open) return;
    setSourcingType('VENDOR');
    setVendorId('');
    setWarehouseId('');
    setNeededBy('');
    setJustification('');
    setNotes('');
    setSubmitForApproval(true);
    setLines([newLine()]);
  }, [open]);

  const totals = useMemo(() => {
    let lineCount = 0;
    let estTotal = 0;
    let hasUnpriced = false;
    for (const line of lines) {
      const qty = Number(line.quantity);
      if (!line.itemId || !Number.isFinite(qty) || qty <= 0) continue;
      lineCount += 1;
      const explicit = line.estimatedUnitPrice ? Number(line.estimatedUnitPrice) : NaN;
      const item = itemById.get(line.itemId);
      const price = Number.isFinite(explicit) ? explicit : (item?.costPrice ?? null);
      if (price == null) hasUnpriced = true;
      else estTotal += qty * price;
    }
    return { lineCount, estTotal, hasUnpriced };
  }, [lines, itemById]);

  const mutation = useMutation({
    mutationFn: async () => {
      const built: CreatePurchaseRequestPayload['items'] = lines
        .map((l) => ({
          itemId: l.itemId,
          quantity: Number(l.quantity),
          estimatedUnitPrice: l.estimatedUnitPrice ? Number(l.estimatedUnitPrice) : undefined,
        }))
        .filter((l) => l.itemId && l.quantity > 0);

      if (built.length === 0) throw new Error('Add at least one raw-material line with quantity > 0.');

      const payload: CreatePurchaseRequestPayload = {
        warehouseId,
        sourcingType,
        proposedVendorId: sourcingType === 'VENDOR' && vendorId ? vendorId : undefined,
        items: built,
        neededBy: neededBy ? new Date(neededBy).toISOString() : undefined,
        justification: justification.trim() || undefined,
        notes: notes.trim() || undefined,
      };
      const created = await procurementService.createRequest(payload);
      if (submitForApproval) {
        return procurementService.submitRequest(created.id);
      }
      return created;
    },
    onSuccess: (pr) => {
      toast.success(
        submitForApproval
          ? `${pr.prNumber} submitted for approval`
          : `${pr.prNumber} saved as draft`,
      );
      qc.invalidateQueries({ queryKey: ['purchase-requests'] });
      onOpenChange(false);
    },
    onError: (err: unknown) =>
      toast.error(err instanceof Error ? err.message : 'Could not create request'),
  });

  const onSubmit = (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    if (!warehouseId || totals.lineCount === 0) return;
    mutation.mutate();
  };

  const addLine = () => setLines((ls) => [...ls, newLine()]);
  const removeLine = (key: number) =>
    setLines((ls) => (ls.length === 1 ? ls : ls.filter((l) => l.key !== key)));

  const valid = warehouseId && totals.lineCount > 0;

  return (
    <FormSheet
      open={open}
      onOpenChange={onOpenChange}
      headerSlot={
        <div className="flex items-start gap-4">
          <span
            aria-hidden="true"
            className="grid h-14 w-14 shrink-0 place-items-center rounded-lg bg-gradient-to-br from-primary/10 to-primary/5 text-primary ring-1 ring-inset ring-primary/15"
          >
            <ClipboardList className="h-6 w-6" />
          </span>
          <div className="min-w-0 flex-1 space-y-1.5">
            <DialogEyebrow>New purchase request</DialogEyebrow>
            <DialogTitle className="text-[18px] font-semibold leading-tight tracking-tight">
              Request raw materials
            </DialogTitle>
            <DialogDescription className="text-[12.5px] leading-relaxed">
              Pick the raw materials you need and how you'd source them. Submitting
              routes the request to a manager for approval before any purchase.
            </DialogDescription>
          </div>
        </div>
      }
      title=""
      submitLabel={submitForApproval ? 'Submit for approval' : 'Save as draft'}
      onSubmit={onSubmit}
      isSubmitting={mutation.isPending}
      error={mutation.error}
      size="5xl"
      fixedHeight="xl"
    >
      <div className="space-y-6">
        <section className="space-y-3">
          <SectionHeading title="Sourcing" />
          <div className="grid grid-cols-2 gap-2">
            <button
              type="button"
              onClick={() => setSourcingType('VENDOR')}
              className={cn(
                'flex items-start gap-2.5 rounded-md border bg-card px-3 py-2.5 text-left text-[12.5px] transition-colors',
                sourcingType === 'VENDOR'
                  ? 'border-primary/30 ring-1 ring-inset ring-primary/15'
                  : 'border-border/70 hover:border-border',
              )}
            >
              <Building2 className="h-4 w-4 shrink-0 text-primary" />
              <div>
                <div className="font-medium">Dedicated vendor</div>
                <div className="text-[11px] text-muted-foreground">
                  Becomes a PO once approved
                </div>
              </div>
            </button>
            <button
              type="button"
              onClick={() => setSourcingType('OPEN_MARKET')}
              className={cn(
                'flex items-start gap-2.5 rounded-md border bg-card px-3 py-2.5 text-left text-[12.5px] transition-colors',
                sourcingType === 'OPEN_MARKET'
                  ? 'border-primary/30 ring-1 ring-inset ring-primary/15'
                  : 'border-border/70 hover:border-border',
              )}
            >
              <Store className="h-4 w-4 shrink-0 text-primary" />
              <div>
                <div className="font-medium">Open market</div>
                <div className="text-[11px] text-muted-foreground">
                  Buyer purchases, then inwards with invoice
                </div>
              </div>
            </button>
          </div>

          <div className="grid grid-cols-12 gap-4">
            {sourcingType === 'VENDOR' && (
              <div className="col-span-6">
                <FormField
                  label="Proposed vendor"
                  htmlFor="vendor"
                  hint="Optional — the approver can set or change this."
                >
                  <Select value={vendorId} onValueChange={setVendorId}>
                    <SelectTrigger id="vendor" className="h-9">
                      <SelectValue placeholder={vendorsQuery.isLoading ? 'Loading…' : 'Select vendor'} />
                    </SelectTrigger>
                    <SelectContent>
                      {vendors.map((v) => (
                        <SelectItem key={v.id} value={v.id}>
                          <span>{v.name}</span>
                          <span className="ml-2 font-mono text-[10.5px] text-muted-foreground">
                            {v.code}
                          </span>
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </FormField>
              </div>
            )}
            <div className={sourcingType === 'VENDOR' ? 'col-span-3' : 'col-span-6'}>
              <FormField label="Destination warehouse" htmlFor="warehouse" required>
                <Select value={warehouseId} onValueChange={setWarehouseId}>
                  <SelectTrigger id="warehouse" className="h-9">
                    <SelectValue placeholder={warehousesQuery.isLoading ? 'Loading…' : 'Select'} />
                  </SelectTrigger>
                  <SelectContent>
                    {warehouses.map((w) => (
                      <SelectItem key={w.id} value={w.id}>
                        <span className="font-mono text-[10.5px] text-muted-foreground">{w.code}</span>
                        <span className="ml-2">{w.name}</span>
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </FormField>
            </div>
            <div className="col-span-3">
              <FormField label="Needed by" htmlFor="neededBy">
                <Input
                  id="neededBy"
                  type="date"
                  value={neededBy}
                  onChange={(e) => setNeededBy(e.target.value)}
                  className="h-9"
                />
              </FormField>
            </div>
          </div>
        </section>

        <section className="space-y-3">
          <SectionHeading
            title="Raw materials"
            hint={`${totals.lineCount} valid · est. ${fmt.inr(totals.estTotal)}${
              totals.hasUnpriced ? ' + unpriced' : ''
            }`}
          />
          {rawItems.length === 0 && !itemsQuery.isLoading && (
            <div className="rounded-md border border-warning/30 bg-warning/5 px-3 py-2 text-[11.5px] text-[hsl(38_85%_30%)]">
              No raw-material items yet. Add an item and set its Classification to
              "Raw material" in Inventory first.
            </div>
          )}
          <div className="space-y-2">
            {lines.map((l) => {
              const item = l.itemId ? itemById.get(l.itemId) ?? null : null;
              return (
                <div
                  key={l.key}
                  className="grid grid-cols-12 gap-2 rounded-md border border-border/70 bg-card p-2"
                >
                  <div className="col-span-6">
                    <Select
                      value={l.itemId}
                      onValueChange={(v) =>
                        setLines((ls) => ls.map((x) => (x.key === l.key ? { ...x, itemId: v } : x)))
                      }
                    >
                      <SelectTrigger className="h-8 text-[12.5px]">
                        <SelectValue placeholder={itemsQuery.isLoading ? 'Loading…' : 'Select raw material'} />
                      </SelectTrigger>
                      <SelectContent>
                        {rawItems.map((it) => (
                          <SelectItem key={it.id} value={it.id}>
                            <span className="font-mono text-[11px] text-muted-foreground">{it.sku}</span>
                            <span className="ml-2">{pickerLabel(it)}</span>
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                  <div className="col-span-3">
                    <Input
                      type="number"
                      inputMode="decimal"
                      min={0}
                      step="0.001"
                      value={l.quantity}
                      onChange={(e) =>
                        setLines((ls) =>
                          ls.map((x) => (x.key === l.key ? { ...x, quantity: e.target.value } : x)),
                        )
                      }
                      placeholder="Qty"
                      className="h-8 text-right text-[12.5px] tabular-nums"
                    />
                  </div>
                  <div className="col-span-2">
                    <Input
                      type="number"
                      inputMode="decimal"
                      min={0}
                      step="0.01"
                      value={l.estimatedUnitPrice}
                      onChange={(e) =>
                        setLines((ls) =>
                          ls.map((x) =>
                            x.key === l.key ? { ...x, estimatedUnitPrice: e.target.value } : x,
                          ),
                        )
                      }
                      placeholder={item?.costPrice != null ? `${item.costPrice}` : 'Est. ₹'}
                      className="h-8 text-right text-[12.5px] tabular-nums"
                    />
                  </div>
                  <div className="col-span-1 flex items-center justify-end">
                    <Button
                      type="button"
                      variant="ghost"
                      size="icon"
                      onClick={() => removeLine(l.key)}
                      disabled={lines.length === 1}
                      className="h-7 w-7 text-muted-foreground hover:text-destructive"
                    >
                      <Trash2 className="h-3.5 w-3.5" />
                    </Button>
                  </div>
                </div>
              );
            })}
          </div>
          <Button type="button" variant="outline" size="sm" onClick={addLine}>
            <Plus className="h-3.5 w-3.5" />
            Add line
          </Button>
        </section>

        <section className="grid grid-cols-12 gap-4">
          <div className="col-span-8 space-y-4">
            <FormField label="Justification" htmlFor="justification" hint="Why is this needed?">
              <Textarea
                id="justification"
                value={justification}
                onChange={(e) => setJustification(e.target.value)}
                placeholder="Production run, sample order, stock replenishment…"
                rows={2}
              />
            </FormField>
            <FormField label="Notes" htmlFor="notes">
              <Textarea
                id="notes"
                value={notes}
                onChange={(e) => setNotes(e.target.value)}
                placeholder="Quality specs, colour, supplier preference…"
                rows={2}
              />
            </FormField>
          </div>
          <div className="col-span-4 flex items-start">
            <button
              type="button"
              onClick={() => setSubmitForApproval((v) => !v)}
              className={cn(
                'flex w-full items-center gap-2.5 rounded-md border bg-card px-3 py-2.5 text-left text-[12.5px] transition-colors',
                submitForApproval
                  ? 'border-primary/30 ring-1 ring-inset ring-primary/15'
                  : 'border-border/70 hover:border-border',
              )}
            >
              {submitForApproval ? (
                <Send className="h-4 w-4 shrink-0 text-primary" />
              ) : (
                <FileEdit className="h-4 w-4 shrink-0 text-muted-foreground" />
              )}
              <div className="flex-1">
                <div className="font-medium">
                  {submitForApproval ? 'Submit for approval' : 'Save as draft'}
                </div>
                <div className="text-[11px] text-muted-foreground">
                  {submitForApproval
                    ? 'Goes to a manager to approve'
                    : 'Stays editable until you submit'}
                </div>
              </div>
            </button>
          </div>
        </section>

        {!valid && (
          <div className="rounded-md border border-warning/30 bg-warning/5 px-3 py-2 text-[11.5px] text-[hsl(38_85%_30%)]">
            Pick a warehouse and at least one raw-material line with quantity &gt; 0.
          </div>
        )}
      </div>
    </FormSheet>
  );
}
